import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import useCalendly from '../hooks/useCalendly';
import Button from './ui/Button';

const SCRIPT_SRC = 'https://assets.calendly.com/assets/external/widget.js';

function loadCalendlyScript() {
  if (document.querySelector(`script[src="${SCRIPT_SRC}"]`)) {
    return;
  }
  const script = document.createElement('script');
  script.src = SCRIPT_SRC;
  script.async = true;
  document.body.appendChild(script);
}

export default function CalendlyModal() {
  const { isOpen, close, url } = useCalendly();
  const containerRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;
    loadCalendlyScript();
    const node = containerRef.current;
    if (!node) return;
    const observer = new MutationObserver(() => {
      const iframe = node.querySelector('iframe');
      if (iframe) {
        iframe.setAttribute('title', 'Schedule a meeting with Carter Asset Management');
      }
    });
    observer.observe(node, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/70 px-4">
      <div className="relative w-full max-w-3xl rounded-3xl bg-white p-4 shadow-2xl ring-1 ring-black/10">
        <button
          type="button"
          onClick={close}
          className="absolute right-4 top-4 rounded-full border border-slate-200 p-2 text-slate-500 transition hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-cam-blue focus:ring-offset-2"
          aria-label="Close scheduling modal"
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" className="h-5 w-5" strokeWidth="1.5">
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
          </svg>
        </button>
        <div className="mb-4 flex items-center justify-between gap-3 pr-12">
          <div>
            <p className="text-sm font-semibold uppercase tracking-wide text-cam-blue/70">Schedule a call</p>
            <h2 className="text-xl font-semibold text-cam-blue">Choose a time that works for you</h2>
          </div>
          <Button variant="secondary" onClick={close} className="hidden sm:inline-flex focus:ring-offset-white">
            Close
          </Button>
        </div>
        <div
          ref={containerRef}
          className="calendly-inline-widget h-[580px] w-full"
          data-url={url}
          aria-live="polite"
        />
      </div>
    </div>,
    document.body
  );
}
